import AnimatedHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { BooleanRadio, DiseaseSearchV2 } from '@app/components';
import { getCountriesOptions } from '@app/constants';
import {
  AntdForm,
  AntdInput,
  AntdModal,
  AntdTypography,
  AsyncSelect,
  Button,
} from '@ui/components';
import { useAddDependentModal } from './hooks/useAddDependentModal.hook';

const { Title } = AntdTypography;

type AddDependentModalProps = {
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
};

export const AddDependentModal = ({ open, onClose, onSuccess }: AddDependentModalProps) => {
  const { t } = useTranslation();
  const { form, isPending, handleSubmit } = useAddDependentModal(onSuccess);
  const haveDisease = AntdForm.useWatch('haveDisease', form);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <AntdModal open={open} onCancel={handleCancel} footer={null} width={480}>
      <Title level={4} className="mt-0 mb-4">
        {t('account_select.add_dependent_title')}
      </Title>
      <AntdForm
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
        disabled={isPending}
        initialValues={{ haveDisease: true }}
      >
        <AntdForm.Item label={t('account_select.relationship_question')}>
          <AntdInput value={t('account_select.relation.child')} className="h-10" disabled />
        </AntdForm.Item>

        <AntdForm.Item
          name="firstName"
          label={t('account_select.first_name')}
          rules={[{ required: true, message: t('validations.required') }]}
        >
          <AntdInput className="h-10" />
        </AntdForm.Item>

        <AntdForm.Item
          name="lastName"
          label={t('account_select.last_name')}
          rules={[{ required: true, message: t('validations.required') }]}
        >
          <AntdInput className="h-10" />
        </AntdForm.Item>

        <BooleanRadio
          className="mb-4"
          isActive={haveDisease ?? true}
          name="haveDisease"
          trueDataTestId="dependent-have-diseases-true"
          falseDataTestId="dependent-have-diseases-false"
          label={t('account_select.have_diagnosed_conditions_question')}
          onChange={(e) => {
            if (!e.target.value) {
              form.setFieldValue('diseases', undefined);
            }
          }}
        />

        <AnimatedHeight height={haveDisease ? 'auto' : 0}>
          {haveDisease && (
            <AntdForm.Item
              name="diseases"
              rules={[{ required: true, message: t('validations.required') }]}
            >
              <DiseaseSearchV2 />
            </AntdForm.Item>
          )}
        </AnimatedHeight>

        <AntdForm.Item
          name="country"
          label={t('account_select.country_question')}
          rules={[{ required: true, message: t('validations.required') }]}
          className="mb-6"
        >
          <AsyncSelect
            isSearchable
            isClearable
            menuShouldScrollIntoView
            captureMenuScroll
            options={getCountriesOptions()}
            className="w-full h-10"
            mobileHeight={40}
            desktopHeight={40}
            placeholder={t('account_select.country_placeholder')}
          />
        </AntdForm.Item>

        <div className="flex gap-2 w-full">
          <Button onClick={handleCancel} className="flex-1" disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" className="flex-1" loading={isPending}>
            {t('common.confirm')}
          </Button>
        </div>
      </AntdForm>
    </AntdModal>
  );
};
